import { InvalidOperationException } from "../../Global/Base/Exceptions";
import { HashType } from "../../Global/Base/Security/HashType";
import { IReadableStream, ISigningAppCommunicator } from "./CryptoInterfaces";
import { CommandType } from "./Messages/CommandType";
import { IStream, IStreamBlockRequest, IStreamData, IStreamHash, IStreamHashRequest } from "./Messages/MessagesInterface";
import { TransferEncoding } from "./Messages/TransferEncoding";

export class ReadableStream implements IReadableStream {
    private readonly _streamId: string;
    private readonly _communicator: ISigningAppCommunicator;
    private _position: number;

    /**
     * Délka streamu
     */
    public readonly size: number;
    /**
     * Název streamu
     */
    public readonly name: string;

    public get position(): number {
        return this._position;
    }

    /**
     * Unikátní identifikátor streamu dat
     */
    public get streamId(): string {
        return this._streamId;
    }

    constructor(streamId: string, communicator: ISigningAppCommunicator, streamSize: number, name: string) {
        this._streamId = streamId;
        this._communicator = communicator;
        this.size = streamSize;
        this.name = name;
    }

    /**
     * Převine stream na začátek
     */
    public async begin(): Promise<void> {
        // přetočení streamu na začátek
        const rewindMessage: IStream = {
            streamId: this.streamId,
        };

        await this._communicator.postMessage(CommandType.SeekStreamBegin, rewindMessage);
        this._position = 0;
    }

    public isEnd(): boolean {
        return this._position >= this.size;
    }

    /**
     * Načte block dat o zadané délce. Čtení pokračuje od předchozí pozice.
     * @param length požadovaná velikost datového bloku.
     */
    public async readBlock(length: number, encoding: TransferEncoding): Promise<string> {
        try {
            const getBlockMessage: IStreamBlockRequest = {
                streamId: this.streamId,
                length: length,
                encoding: TransferEncoding[encoding],
            };
            const response = await this._communicator.postMessage(CommandType.GetNextStreamBlock, getBlockMessage);
            if (!response.body || !((response.body as IStreamData).data))
                throw new InvalidOperationException("ReadableStream didn't receive data.");
            this._position += length;
            return (response.body as IStreamData).data;
        } catch (e) {
            throw new InvalidOperationException("Failed to read stream");
        }
    }

    /**
     * Vrátí hash obsahu celého streamu
     */
    public async getHash(hashType: HashType): Promise<string> {
        const dataMessage: IStreamHashRequest = {
            streamId: this.streamId,
            hashType: HashType[hashType],
        };

        const response = await this._communicator.postMessage(CommandType.GetStreamHash, dataMessage);
        if (!response.body)
            throw new InvalidOperationException("Didn't receive stream hash response");

        return (response.body as IStreamHash).streamHash;
    }
}
